import { FC, useCallback, useEffect, useMemo } from 'react';
import Style from './FormExport.module.scss';
import { ReactComponent as ExportIcon } from 'assets/img/common/export.svg';
import { AppButtonWithTooltip } from 'uikit/buttons/buttonWithTooltip/buttonWithTooltip';
import {
  IDoctorStudyDto,
  IFormDto,
  IFormResultVersionDto,
  IPatientListDto,
  StudyRoleClaimValue,
} from 'src/services/api/api-client';
import { useHasStudyClaims } from 'src/helpers/auth/auth-helper';
import { DialogModal } from 'src/components/dialogModal/dialogModal.component';
import { useTranslation } from 'react-i18next';
import { useModal, UseModalProps } from 'src/application/hooks/useModal';
import { useForm } from 'react-hook-form';
import { useScopedTranslation } from 'src/application/localisation/useScopedTranslation';
import { HookFormRadioGroup } from 'src/hookFormControls/hookFormRadioGroup';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { HookFormCheckboxGroupField } from 'src/hookFormControls/HookFormCheckboxGroupField';
import { ValidationFormRules } from 'src/helpers/validation-helpers';
import { AppButton } from 'uikit/buttons';
import { AppInputLabel } from 'uikit/wrappers';
import _ from 'lodash';
import { isAppAttachmentValue } from 'src/components/formEditor/uiEditor/uiEditor-helper';
import { AppAttachmentValue } from 'uikit/fields/attachments/attachmentsField/appAttachmentsField';
import { isNullOrEmpty } from 'src/helpers/string-helper';
import { MetricActions, MetricCategories } from 'src/application/analitics/umami/TrackEventParams';
import { useUmamiTracker } from 'src/application/analitics/umami/useUmamiTracker';
import { useGenerateRecordReport } from 'src/features/report/records/useGenerateRecordReport';
import { downloadFile } from '../../../../helpers/file-helper';
import { getStatistics } from '../../../../services/api/api-client/ExportLogClient';
import { QueryFactory } from '../../../../services/api';
import { DateFormats, localFormat } from '../../../../helpers/date-helpers';
import { useGetPatientByStudyAndUniqIdQuery } from 'src/services/api/api-client/PatientQuery';
import { FileTypeVariantsType } from 'src/features/reportExporting/reportExporting';
import { useFileTypeOptions } from 'src/features/reportExporting/useReportExportingOptions';

export function useFormExport({
  patientUniqId,
  stepName,
  formResultVersion,
  formConfig,
  customFormDate,
}: {
  patientUniqId: string;
  stepName?: string;
  formResultVersion: IFormResultVersionDto;
  formConfig: IFormDto;
  customFormDate?: Date | null;
}) {
  const { t } = useScopedTranslation('Forms.Overview.Export');
  const { hasOneOfStudyClaims } = useHasStudyClaims();
  const modal = useModal('CLOSED');

  const study = useStudy();
  const { data: patient } = useGetPatientByStudyAndUniqIdQuery(study?.id, patientUniqId, {
    enabled: !!study?.id,
  });

  if (
    !study ||
    !hasOneOfStudyClaims(StudyRoleClaimValue.ExportForm, StudyRoleClaimValue.ExportPatientEcrfReport) ||
    (study?.hasEcrf && !patient?.archivedBy)
  ) {
    return {
      canExport: false,
      Button: null,
      Modal: null,
      openExportModal: () => {},
    };
  }

  const ExportButton = (
    <AppButtonWithTooltip
      colorSchema={'secondary'}
      variant={'icon-link'}
      Icon={ExportIcon}
      tooltip={t('ExportButton')}
      onClick={modal.openModal}
    />
  );

  const ExportModal = (
    <FormExportModal
      patient={patient!}
      modal={modal}
      formConfig={formConfig}
      stepName={stepName}
      formResultVersion={formResultVersion}
      study={study}
      patientUniqueId={patient?.uniqueId ?? ''}
      customFormDate={customFormDate}
    />
  );

  return {
    canExport: true,
    Button: ExportButton,
    Modal: ExportModal,
    openExportModal: modal.openModal,
  };
}

type FormState = {
  fileType: FileTypeVariantsType;
  fileIds: string[];
};

const FormExportModal: FC<{
  patient: IPatientListDto;
  modal: UseModalProps;
  study: IDoctorStudyDto;
  patientUniqueId: string;
  formConfig: IFormDto;
  stepName?: string;
  formResultVersion: IFormResultVersionDto;
  customFormDate?: Date | null;
}> = ({ modal, formConfig, stepName, formResultVersion, patientUniqueId, study, customFormDate, patient }) => {
  const { t: commonLocalizer } = useTranslation();
  const { t } = useScopedTranslation('Forms.Overview.Export.Modal');
  const { trackEvent } = useUmamiTracker();
  const fileTypeOptions = useFileTypeOptions();
  const fileIdOptions = useFileIdsOptions(formResultVersion.value);

  const generatePdfReport = useGenerateRecordReport();

  const form = useForm<FormState>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  // Note: Select all by default
  useEffect(() => {
    if (!modal.visible) return;

    form.reset({
      fileIds: fileIdOptions.map((x) => x.key),
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal.visible]);

  const submit = form.handleSubmit(async (state: FormState) => {
    if (state.fileType === 'Pdf') {
      await generatePdfReport(patient, stepName, formResultVersion, formConfig);
    }

    if (state.fileType === 'Csv') {
      const fileResponse = await getStatistics(
        study.id,
        [],
        [formConfig.type],
        undefined,
        patientUniqueId,
        true,
        formResultVersion.formResultId,
      );

      if (!fileResponse) {
        return;
      }

      downloadFile(fileResponse);
    }

    if (state?.fileIds?.length > 0) {
      const files = await QueryFactory.FilesClient.getFiles(state.fileIds);
      downloadFile(files);
    }

    trackEvent({
      category: MetricCategories.EvaluationForm,
      action: MetricActions.ButtonPressed,
      name: 'Export logs for record',
    });

    closeHandler();
  });

  const closeHandler = useCallback(() => {
    modal.closeModal();
  }, [modal]);

  const inProgress = useMemo(
    () => form.formState.isSubmitting || form.formState.isLoading,
    [form.formState.isLoading, form.formState.isSubmitting],
  );

  const selectedFileIds = form.watch('fileIds');

  const isAllRecordsSelected = useMemo(
    () => selectedFileIds?.length === fileIdOptions?.length,
    [fileIdOptions?.length, selectedFileIds?.length],
  );

  const renderAttachmentsControl = () => {
    if (fileIdOptions.length === 0)
      return <span className={Style.noAttachmentsPlaceholder}>{commonLocalizer('Common_NoAttachments')}</span>;

    return (
      <>
        <AppButton
          className={Style.uncheckAllButton}
          variant={'icon-link'}
          colorSchema={'primary'}
          text={isAllRecordsSelected ? commonLocalizer('Common_None') : commonLocalizer('Common_SelectAll')}
          onClick={() =>
            isAllRecordsSelected
              ? form.setValue('fileIds', [])
              : form.setValue(
                  'fileIds',
                  fileIdOptions.map((x) => x.key),
                )
          }
          disabled={inProgress}
        />
        <HookFormCheckboxGroupField
          name={'fileIds'}
          control={form.control}
          labelProps={{}}
          options={fileIdOptions}
          valueField={'key'}
          labelField={'optionText'}
          disabled={inProgress}
          withEllipsis={true}
        />
      </>
    );
  };

  const modalTitle = useMemo(() => {
    return !stepName
      ? t('TitleWithDate', {
          formType: formConfig.type,
          date: localFormat(customFormDate ?? formResultVersion.createdAt, DateFormats.longDate),
        })
      : t('Title', { formType: formConfig.type });
  }, [stepName, t, formConfig.type, customFormDate, formResultVersion.createdAt]);

  return (
    <DialogModal
      containerClassName={Style.modalRoot}
      bodyClassName={Style.modalBody}
      visible={modal.visible}
      onHide={closeHandler}
      title={modalTitle}
      subTitle={stepName}
      isDisabled={inProgress}
      footer={{
        errors: form.formState.errors.root?.message,
        leftButton: {
          variant: 'button',
          colorSchema: 'secondary',
          text: commonLocalizer('Common_Cancel'),
          onClick: closeHandler,
          disabled: inProgress,
        },
        rightButton: {
          variant: 'button',
          colorSchema: 'primary',
          text: commonLocalizer('Common_Export'),
          onClick: submit,
          isLoading: inProgress,
          disabled: inProgress || !form.formState.isValid,
        },
      }}
    >
      <HookFormRadioGroup
        name={'fileType'}
        control={form.control}
        rules={{ ...ValidationFormRules().requiredRule }}
        labelProps={{
          text: t('SelectFileType'),
        }}
        options={fileTypeOptions}
        valueField={'key'}
        labelField={'text'}
        disabled={inProgress}
      />

      <AppInputLabel text={t('SelectAttachments')}>{renderAttachmentsControl()}</AppInputLabel>
    </DialogModal>
  );
};

type FileOption = {
  key: string;
  optionText: string;
  dataKey: string;
};

type AppAttachmentValueWithDataKey = AppAttachmentValue & { dataKey: string };

const useFileIdsOptions = (answer: { [key: string]: any } | null): FileOption[] => {
  const appAttachments = useMemo(
    () =>
      _.isNil(answer)
        ? []
        : Object.entries(answer).reduce(
            (arr: AppAttachmentValueWithDataKey[], [dataKey, value]) =>
              Array.isArray(value) && value.every(isAppAttachmentValue)
                ? [...arr, ...value.flatMap((v) => ({ ...v, dataKey }))]
                : arr,
            [],
          ),
    [answer],
  );

  return appAttachments
    .filter((x) => !isNullOrEmpty(x.id))
    .map((x) => ({ key: x.id!, optionText: x.fileName, dataKey: x.dataKey }));
};
